import { useMemo } from "react";
import {
  createSessionResultsCache,
  resolveTheme,
  type BaselineRow,
  type ProgressDeps,
  type SessionResultsSnapshot,
  type UseSessionResultsOptions,
} from "@jackedlogs/ui";
import type {
  MuscleSlug,
  PainSeverity,
  TimedSessionResult,
  UnitsPreference,
} from "@jackedlogs/shared";

import { apiFetch } from "../api";
import { loadCachedExercises } from "../logger/exercisesCache";
import { lsCache } from "../lsCache";
import { useProfileDoc } from "../useProfileDoc";
import { useThemePalette } from "../theme";

// One single-flight session-results cache shared by every SELF surface wired
// through this hook: the Progress panels warm it, and the Goals editor
// (TargetsEditor) paints its reference numbers straight from it (getCached is
// synchronous) instead of after a spinner. Reusing the SAME instance avoids a
// cold-paint regression and two caches that could diverge on save. Mirror of
// mobile's apps/mobile/src/progress/progressDeps.ts. (PlanScreen's composer keeps
// a separate editorResultsCache for the same endpoint — an open consolidation
// item, CN-self-results-cache, not unified yet.)
const resultsCache = createSessionResultsCache();

// RW-18: the Logger's `invalidateSessionResults` host dep — drops this SAME
// cache after a save so Progress refetches fresh, matching mobile's
// apps/mobile/src/progress/sessionResultsCache.ts's invalidate. Exported as
// a plain function (not a hook) because useSaveWorkout's caller has no
// component to hook from — same reasoning as mobile's split-out module.
export const invalidateSelfSessionResults = (): void => resultsCache.invalidate();

// Cold-boot disk SWR, localStorage twin of mobile's AsyncStorage
// resultsPersist/baselinesPersist (apps/mobile/src/progress/progressDeps.ts:31-36)
// — same storage keys, `lsCache` is a drop-in for `asyncStorageResourceCache`
// (both `{read,write}` over a single JSON blob).
const resultsPersist = lsCache<TimedSessionResult[]>("progress/sessionResults");
const baselinesPersist = lsCache<BaselineRow[]>("progress/baselines");

// Module single-flight for the stale disk read so the panels that mount
// together share ONE localStorage round-trip, mirroring mobile's
// staleInFlight (apps/mobile/src/progress/progressDeps.ts:40-50).
let staleInFlight: Promise<SessionResultsSnapshot | null> | null = null;
function readStaleResults(): Promise<SessionResultsSnapshot | null> {
  if (!staleInFlight) {
    staleInFlight = Promise.all([resultsPersist.read(), loadCachedExercises()])
      .then(([results, catalog]) =>
        results && catalog ? { results, catalog } : null,
      )
      .catch(() => null);
  }
  return staleInFlight;
}

function persistResults(results: TimedSessionResult[]): void {
  // A fresh fetch supersedes any stale disk snapshot from this run.
  staleInFlight = null;
  void resultsPersist.write(results);
}

// RW-18: the Logger's `sessionResultsDeps` host dep (hostDeps.ts —
// "useSessionResults' own deps bundle") — the SAME resultsCache instance
// above, so a save invalidates the identical cache the in-workout meter and
// the Progress panels read, plus the cold-boot localStorage SWR above
// (mirrors mobile's apps/mobile/src/progress/progressDeps.ts's plain
// (non-hook) export).
export const selfSessionResultsDeps: UseSessionResultsOptions = {
  apiFetch,
  cache: resultsCache,
  readStale: readStaleResults,
  persistResults,
};

// Stable empty fallback so a `useMemo([..., painMap])` downstream keeps identity
// across renders (a fresh `{}` per render would cascade new deps into every memo)
// for the common case of an athlete with no pain entries.
const EMPTY_PAIN_MAP = {} as const;

// The SELF ProgressDeps — my own training over the unscoped apiFetch, not an
// athlete I coach (that lives in AthleteDetailScreen with a per-sub scoped cache).
// The minimal fields the shared Tier-1 panels + TargetsEditor consume;
// ProgressScreen layers its PG-1 extras (pinnedProgram / prescription / figure)
// on top of this base.
export function useSelfProgressDeps(): ProgressDeps {
  const { dark, family } = useThemePalette();
  const { doc } = useProfileDoc();
  const units =
    (doc?.["unitsPreference"] as UnitsPreference | undefined) ?? "kg";
  const weekStartDay = doc?.["weekStartDay"] as number | undefined;
  // BodyScreen's PainMapPicker (lifted 2026-08-16) writes painMap into this
  // SAME profile doc via savePatch — read it back the way BodyScreen.tsx:45
  // does, mirroring mobile's `profile.painMap ?? EMPTY_PAIN_MAP`.
  const painMap =
    (doc?.["painMap"] as
      | Partial<Record<MuscleSlug, PainSeverity>>
      | undefined) ?? EMPTY_PAIN_MAP;
  return useMemo<ProgressDeps>(
    () => ({
      apiFetch,
      theme: resolveTheme(dark, family),
      units,
      painMap,
      resultsCache,
      readStaleResults,
      persistResults,
      readStaleBaselines: () => baselinesPersist.read(),
      persistBaselines: (rows) => {
        void baselinesPersist.write([...rows]);
      },
      isDark: dark,
      weekStartDay,
    }),
    [dark, family, units, weekStartDay, painMap],
  );
}
